import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Grid, TextField, Typography, Divider, FormControlLabel, Checkbox, Radio, RadioGroup } from '@mui/material';
import { formatDate, formatPrice } from '../../../shared/utils';
import { DesktopDateTimePicker, DesktopDatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { LocalStoreKeys } from 'src/app/shared/helpers/localstorage/local-store';
import { useAppDispatch, useAppSelector } from 'src/app/react/hooks/redux';
import { Auth } from 'src/app/models/auth';
import { PaymentPurchaseRequest } from 'src/app/react/models/purchase/payment-purchase-request.model';
import { paymentPurchase } from 'src/app/react/redux/purchase/actions';
import moment, { Moment } from 'moment';

interface PaymentPurchaseProps {
    onPaymentConfimation: () => void;
}

const locale = moment.locale('pt-br');

const paymentMethodsList = [
    {
        key: 2,
        value: "Saldo em Conta"
    },
    {
        key: 1,
        value: "Gerar um Boleto"
    },
    {
        key: 4,
        value: "Gerar um PIX"
    }
];

export const PaymentPurchase = ({onPaymentConfimation}: PaymentPurchaseProps) => {

    const currentUser: Auth = JSON.parse(localStorage.getItem('currentUser'));
    const dispatch = useAppDispatch();
    const date = moment();

    const { purchase, summary, loading } = useAppSelector(state => state.purchase);
    const [ currentAccountBalance, setCurrentAccountBalance ] = useState(JSON.parse(localStorage.getItem(LocalStoreKeys.currentAccountBalance)));

    const [ dateTimeValue, setDateTimeValue ] = useState<Moment>(moment({hour: date.hour()+1, minute: 0, second: 0}));
    const [ dateTimeError, setDateTimeError ] = useState<boolean>(false);
    const [ boletoDateTimeValue, setBoletoDateTimeValue ] = useState<Moment>(moment({hour: 0, minute: 0, second: 0}).add(3, 'days'));
    const [ pixDateTimeValue, setPixDateTimeValue ] = useState<Moment>(moment({hour: 0, minute: 0, second: 0}));

    const [ paymentMethods, setPaymentMethods ] = useState<any[]>(paymentMethodsList);
    const [ paymentMethod, setPaymentMethod ] = useState<number | null>(2);
    const [ paymentMethodError, setPaymentMethodError ] = useState<boolean>(false);

    const [ showPaymentDifference, setShowPaymentDifference ] = useState<boolean>(false);
    const [ paymentDifference, setPaymentDifference ] = useState<boolean>(false);
    const [ paymentDifferenceValue, setPaymentDifferenceValue ] = useState<number | null>(null);
    
    const [ showPayNow, setShowPayNow ] = useState<boolean>(false);
    const [ payNow, setPayNow ] = useState<boolean>(false);

    useEffect(()=>{
        if(currentAccountBalance < summary?.total){
            setShowPaymentDifference(true);
            setPaymentMethods(paymentMethodsList.slice(1));
        }
        if(paymentMethod == 2){
            setShowPayNow(true);
        }
    },[paymentMethod, paymentDifferenceValue, dateTimeError])

    const getMinTime = () => {
        if(dateTimeValue?.date() > moment().date())
            return moment({hour: 0, minute: 0, second: 0})
        return moment({hour: moment().hour()+1, minute: 0, second: 0})
    }

    const handleChangeTime = (value: any) => {
        
        if((value?.toDate() < moment().toDate()) || (!value.isValid())){
            setDateTimeError(true);
            return;
        }
        if(value?.minute() > 0)
           value.set({minute:  0});

        setDateTimeValue(value);
        setDateTimeError(false);
    }

    const handlePaymentMethod = (e: any) => {
        setPaymentMethod(parseInt(e.target.value));
        if(e.target.value !== 2){
            setShowPayNow(false)
            handlePayNow(false)
        }
        setPaymentMethodError(false);
    } 

    const handlePaymentDifference = (checked: any) => {
        setPaymentDifference(checked);
        setPaymentDifferenceValue(summary?.total - currentAccountBalance)
    }
    
    const handlePayNow = (checked: any) => {
        setPayNow(checked);
        if(checked)
            setDateTimeValue(moment({hour: date.hour(), minute: date.minute(), seconds: 0}))
        else
            setDateTimeValue(moment({hour: date.hour()+1, minute: 0, seconds: 0}))
    }

    const handlePaymentFinish = () => {
        if(!paymentMethod){
            setPaymentMethodError(true);
            return;
        }

        const paymentPurchaseRequest: PaymentPurchaseRequest = {
            paymentMethodId: paymentMethod !== 2 ? paymentMethod : null,
            useBalance: paymentMethod === 2,
            value: summary?.total,
            differenceValue: paymentDifferenceValue,
            dueDate: paymentMethod === 1 ? boletoDateTimeValue.toDate() : null,
            releaseDate: new Date(dateTimeValue.year(), dateTimeValue.month(), dateTimeValue.date(), dateTimeValue.hour() - 3, 0, 0)
        }
        dispatch(paymentPurchase(currentUser.token.assignorGuid, currentUser.token.userReferenceId, purchase, paymentPurchaseRequest));        
        onPaymentConfimation();
    }

    return (
        <>
            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
                <Grid container spacing={1}>
                    <Grid item xs={8} className="p-4">
                        <Typography variant="h5">Pagamento da Recarga</Typography>

                        <Typography mt={3}>Saldo Disponível: {formatPrice(currentAccountBalance)}</Typography>
                        <Typography mb={1}>Valor a Pagar: {formatPrice(summary?.total)}</Typography>

                        { showPaymentDifference &&
                            <FormControlLabel key="paymentDifference" className='ml-0' onClick={(e: any) => handlePaymentDifference(e.target.checked)} control={<Checkbox className='mr-1'/>} label="Pagar a Diferença" />
                        }
                        <Typography mb={1} mt={3} variant="h6">Método de Pagamento</Typography>

                        <RadioGroup
                            row
                            aria-labelledby="payment-method-radio-buttons-group-label"
                            name="payment-method-radio-buttons-group"
                            value={paymentMethod}
                            onChange={handlePaymentMethod}
                        >
                            {paymentMethods.map((paymentMethod, index) => {
                                return <FormControlLabel key={index} value={paymentMethod?.key} control={<Radio />} label={paymentMethod?.value} />
                                })
                            }
                        </RadioGroup>
                        
                        { paymentMethod !== 2 &&
                            <DesktopDatePicker
                                renderInput={(params) => <TextField {...params} disabled={true}/>}
                                label="Data de Vencimento"
                                value={paymentMethod === 1 ? boletoDateTimeValue : pixDateTimeValue}
                                onChange={(newValue) => newValue && setBoletoDateTimeValue(newValue)}
                                minDate={paymentMethod === 1 ? moment().add(3, 'days'): moment()}
                                disabled={paymentMethod === 4}
                            />
                        }
                        { paymentMethodError && 
                            <Alert sx={{width: '30%'}} className='mt-2' severity="error">Selecione um meio de pagamento!</Alert>
                        }
                        <Typography mb={2} mt={3} variant="h6">Data de Disponibilização</Typography>
                        
                        <Grid
                            container
                            direction="column"
                            justifyContent="flex-start"
                            alignItems="flex-start"
                        >
                            { !payNow &&
                                <>
                                    <Grid item className='mb-4'>
                                        <DesktopDateTimePicker
                                            views={["year","month", "day","hours"]}
                                            renderInput={(params) => <TextField {...params} disabled={true}/>}
                                            label="Data e hora"
                                            value={dateTimeValue}
                                            onChange={handleChangeTime}
                                            minDate={moment()}
                                            minTime={getMinTime()}
                                        />
                                    </Grid>
                                    
                                    { dateTimeError && 
                                        <Alert sx={{width: '30%'}} className='mt-2' severity="error">Data ou Hora Inválida!</Alert>
                                    }
                                </>
                            }
                            { showPayNow &&
                                <Grid item className='mb-3'>
                                    <FormControlLabel key="paynow" className='ml-0' onClick={(e: any) => handlePayNow(e.target.checked)} control={<Checkbox className='mr-1'/>} label="Disponibilizar crédito imediatamente" />
                                </Grid>
                            }
                            { paymentMethod !== 2 &&
                                <Typography variant='subtitle1'>Lembre-se de ter saldo disponível antes do dia e horário da disponibilização da recarga</Typography>
                            }
                            
                        </Grid>
                    </Grid>

                    <Grid item xs={4} className="p-0">
                        <Box sx={{width: '100%', height: '100%', backgroundColor: '#f6f6f6', padding: '1.5rem'}}>
                            <Typography variant="h5">Resumo</Typography>

                            <Typography variant="h6" mt={2} sx={{fontSize: '1rem'}}>
                                Método de Pagamento: {paymentMethod ? paymentMethods.find(x=>x.key == paymentMethod)?.value : ''}
                                { (paymentDifference && paymentMethod) 
                                    && (
                                        <>
                                        <br></br> 
                                        Diferença: {paymentMethods.find(x=>x.key == paymentMethod)?.value} - {formatPrice(paymentDifferenceValue)}
                                        </>
                                    )
                                }
                                <br></br>
                                Data de Processamento: {formatDate(dateTimeValue?.toDate().toString())}
                            </Typography>

                            <Divider />

                            { paymentDifference && 
                                <Typography variant="h5" mt={2}>Diferença a Pagar: {formatPrice(paymentDifferenceValue)}</Typography> 
                            }

                            <Typography variant="h5" mt={2}>{!paymentDifference ? 'Total a Pagar: ' + formatPrice(summary?.total) : 'Total Recarga: ' + formatPrice(summary?.total)} </Typography>

                            <Box sx={{textAlign: 'center'}} className="my-5">
                                <Button
                                    variant="contained"
                                    size="large"
                                    sx={{width: '70%', textTransform: 'none'}}
                                    onClick={handlePaymentFinish}
                                    disabled={loading}
                                >
                                    Finalizar Solicitação de Recarga
                                </Button>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </LocalizationProvider>
        </>
    );
}
